import { useEffect } from "react";
import { ENV } from "hub/src/appConstants/env";
import { isIframeSSOPage } from "hub/src/utils/isIframeSSOPage";

const IFRAME_PARENT_RETURN_URL_STORAGE_KEY = "iframeParentReturnUrl";

const isHubRedirectUrl = (candidateUrl: string): boolean => {
  const redirectUrl = ENV.REACT_APP_IFRAME_SSO_REDIRECT_URL;

  if (!redirectUrl || typeof redirectUrl !== "string") {
    return false;
  }

  const normalizedRedirectUrl = redirectUrl.endsWith("/")
    ? redirectUrl.slice(0, -1)
    : redirectUrl;
  const normalizedCandidateUrl = candidateUrl.endsWith("/")
    ? candidateUrl.slice(0, -1)
    : candidateUrl;

  return (
    normalizedCandidateUrl === normalizedRedirectUrl ||
    normalizedCandidateUrl.startsWith(`${normalizedRedirectUrl}#`) ||
    normalizedCandidateUrl.startsWith(`${normalizedRedirectUrl}?`)
  );
};

const getReturnUrlFromHref = (href: string): string | null => {
  try {
    const parsedUrl = new URL(href);
    const fromSearch =
      parsedUrl.searchParams.get("ReturnUrl") ||
      parsedUrl.searchParams.get("returnUrl");
    if (fromSearch) {
      return decodeURIComponent(fromSearch);
    }

    const hashWithoutPound = parsedUrl.hash.startsWith("#")
      ? parsedUrl.hash.slice(1)
      : parsedUrl.hash;
    const hashMatch = hashWithoutPound.match(
      /(?:^|[?&])(?:ReturnUrl|returnUrl)=([^&]+)/,
    );

    if (hashMatch?.[1]) {
      return decodeURIComponent(hashMatch[1]);
    }
  } catch (error) {
    console.error("Unable to parse parent href for ReturnUrl:", error);
  }

  return null;
};

export const useIFrameRouterRedirect = () => {
  useEffect(() => {
    if (
      isIframeSSOPage &&
      ENV.REACT_APP_IFRAME_SSO_REDIRECT_URL &&
      typeof ENV.REACT_APP_IFRAME_SSO_REDIRECT_URL === "string"
    ) {
      const handleMessageFromParent = (event: MessageEvent) => {
        if (
          event &&
          event.data &&
          event.data.type === "RESPOND_PARENT_LOCATION" &&
          event.data.location
        ) {
          const parentLocation = JSON.parse(event.data.location);
          if (parentLocation) {
            const returnUrl = getReturnUrlFromHref(parentLocation.href);
            if (returnUrl && !isHubRedirectUrl(returnUrl)) {
              sessionStorage.setItem(
                IFRAME_PARENT_RETURN_URL_STORAGE_KEY,
                returnUrl,
              );
            }

            //https://www.csiresources.org/home/hub#/tizra&ReturnUrl=https://www.csiresources.org/home/hub#/tizra
            //Split on &ReturnUrl=
            //if there is [0] and [1] and they match console true
            const parentLocationSplit =
              parentLocation.href.split("&ReturnUrl=");
            if (parentLocationSplit.length > 1) {
              if (parentLocationSplit[0] === parentLocationSplit[1]) {
                const storedReturnUrl = sessionStorage.getItem(
                  IFRAME_PARENT_RETURN_URL_STORAGE_KEY,
                );
                const redirectParentUrl =
                  storedReturnUrl && !isHubRedirectUrl(storedReturnUrl)
                    ? storedReturnUrl
                    : parentLocationSplit[0];

                window.parent.postMessage(
                  {
                    type: "REDIRECT_PARENT",
                    source: "pillar",
                    redirectParentUrl,
                  },
                  parentOrigin,
                );
              }
            }
          }
        }
      };
      window.addEventListener("message", handleMessageFromParent);

      const parentOrigin =
        ENV.REACT_APP_IFRAME_SSO_REDIRECT_URL!.match(/https:\/\/[^/]+/)![0];
      const sendMessageToParent = (): void => {
        window.parent.postMessage(
          {
            type: "REQUEST_PARENT_LOCATION",
            source: "pillar",
          },
          parentOrigin,
        );
      };
      sendMessageToParent();
    }
  }, []);
};
